import { Box, Button, FormControlLabel, SvgIcon, Switch, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState, ChangeEvent } from 'react';
import { ProductAttributeModel } from '../../../../app/services/api/orderManagementClient';
import { Save01 } from '../../../../assets';
import WarningIcon from '@mui/icons-material/Warning';
import { ConfirmationDialog } from '../../../../components/shared/dialogs/ConfirmationDialog';
import { UserContext } from '../../../../components/shared/useUser';

const ExportAttributes = (props: {
  exportAttributes: ProductAttributeModel[],
  isLoading?: boolean,
  onSave: Function
}) => {
  const { t } = useTranslation('productDetails');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showCancelConfirmationModal, setShowCancelConfirmationModal] = useState(false);

  const [attributeValues, setAttributeValues] = useState<Record<number, string>>({});
  const [validationErrors, setValidationErrors] = useState<Record<number, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const { hasPermissionTo } = useContext(UserContext);

  useEffect(() => {
    if (props.exportAttributes) {
      const initialValues: Record<number, string> = {};
      props.exportAttributes.forEach(attr => {
        initialValues[attr.attributeDefinitionId] = attr.value || '';
      });
      setAttributeValues(initialValues);
    }
  }, [props.exportAttributes]);

  const validateValue = (value: string, dataType: string, mask?: string): string => {
    if (value.length === 0) return t('exportAttributesTab.requiedMessage');

    if (dataType?.toLowerCase() === 'number') {
      if (mask?.startsWith('0') && mask?.endsWith('0') && areAllDigitsZero(value)) {
        return t('exportAttributesTab.validValueValidation');
      }
      if (value.startsWith('-')) {
        return t('exportAttributesTab.validValueValidation');
      }
    }

    return '';
  };

  const handleAttributeChange = (event: ChangeEvent<HTMLElement | HTMLTextAreaElement>, attributeId: number, value: string, dataType: string, isOptional: boolean, mask?: string) => {
    let formattedValue = value;
    let updateAttribute = true;

    const isDeletion = event && (
      event.nativeEvent instanceof InputEvent &&
      (event.nativeEvent.inputType === 'deleteContentBackward' ||
        event.nativeEvent.inputType === 'deleteContentForward')
    );

    if (dataType.toLowerCase() === 'number') {
      const digitsOnly = value.replace(/\D/g, '');

      if (mask === '0000.00.00') {
        if (!attributeValues[attributeId]?.startsWith('0') && !isDeletion) {
          updateAttribute = false;
        } else {
          const paddedDigits = digitsOnly.padStart(8, '0').slice(-8);
          formattedValue = `${paddedDigits.substring(0, 4)}.${paddedDigits.substring(4, 6)}.${paddedDigits.substring(6, 8)}`;
        }
      } else if (mask && mask.startsWith('0') && mask.endsWith('0')) {
        formattedValue = digitsOnly;
      }
    }

    if (updateAttribute) {
      setAttributeValues(prev => ({
        ...prev,
        [attributeId]: formattedValue,
      }));
    }

    if (!isOptional) {
      const error = validateValue(formattedValue, dataType, mask);
      setValidationErrors(prev => ({
        ...prev,
        [attributeId]: error,
      }));
    }
  };

  const handleBooleanChange = (attributeId: number, checked: boolean) => {
    setAttributeValues(prev => ({
      ...prev,
      [attributeId]: checked ? '1' : '0',
    }));
  };

  const saveAttributes = () => {
    setIsSaving(true);

    const attributesToSave = Object.entries(attributeValues).map(([id, value]) => ({
      attributeDefinitionId: parseInt(id),
      value,
    }));

    props.onSave(attributesToSave);

    setIsSaving(false);
  };

  const discardChanges = () => {
    const initialValues: Record<number, string> = {};
    props.exportAttributes.forEach(attr => {
      initialValues[attr.attributeDefinitionId] = attr.value || '';
    });
    setAttributeValues(initialValues);

    setValidationErrors({});
  };

  const areAllDigitsZero = (value: string): boolean => {
    const digitsOnly = value.replace(/\D/g, '');

    if (digitsOnly.length === 0) return false;

    return /^0+$/.test(digitsOnly);
  };

  const renderField = (attribute: ProductAttributeModel) => {
    const { attributeDefinitionId, fieldName, dataType, mask, isOptional, isMultilineText } = attribute;
    const value = attributeValues[attributeDefinitionId] || '';
    const error = validationErrors[attributeDefinitionId] || '';

    const toCamelCase = (str: string) =>
      str
        .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
          index === 0 ? word.toLowerCase() : word.toUpperCase(),
        )
        .replace(/\s+/g, '')
        .replace(/[^a-zA-Z0-9]/g, '');

    const camelCaseFieldName = toCamelCase(fieldName);
    const label = t(`exportAttributesTab.${camelCaseFieldName}`, fieldName);

    switch (dataType.toLowerCase()) {
      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={value === 'Yes' || value === 'true' || value === '1'}
                onChange={(e) => handleBooleanChange(attributeDefinitionId, e.target.checked)}
              />
            }
            sx={{ pl: 2, mb: 4 }}
            label={label}
          />
        );

      case 'number':
        if (mask === '0000.00.00') { // Custom only for HTS code
          return (
            <TextField
              fullWidth
              required={!isOptional}
              label={label + ' (0000.00.00)'}
              value={value}
              error={!!error}
              helperText={error}
              placeholder={mask}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9.]*',
              }}
              onChange={(e) => handleAttributeChange(e, attributeDefinitionId, e.target.value, dataType, isOptional, mask)}
              sx={{ mb: 8 }}
            />
          );
        } else if (mask && mask.startsWith('0')) { // Padding for numbers
          return (
            <TextField
              fullWidth
              required={!isOptional}
              label={label + (mask ? ` (${mask})` : '')}
              value={value}
              error={!!error}
              helperText={error}
              placeholder={mask}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                maxLength: mask.length,
              }}
              onChange={(e) => handleAttributeChange(e, attributeDefinitionId, e.target.value, dataType, isOptional, mask)}
              sx={{ mb: 8 }}
            />
          );
        } else {
          return (
            <TextField
              fullWidth
              required={!isOptional}
              label={label + (mask ? ` (${mask})` : '')}
              value={value}
              error={!!error}
              helperText={error}
              type='number'
              inputProps={{ min: 0 }}
              onChange={(e) => handleAttributeChange(e, attributeDefinitionId, e.target.value, dataType, isOptional, mask)}
              sx={{ mb: 8 }}
            />
          );
        }

      default: // String and other types
        return (
          <TextField
            fullWidth
            required={!isOptional}
            label={label}
            value={value}
            error={!!error}
            helperText={error}
            onChange={(e) => handleAttributeChange(e, attributeDefinitionId, e.target.value, dataType, isOptional, mask)}
            multiline={isMultilineText || value?.length > 100}
            rows={(isMultilineText || value?.length > 100) ? 3 : 1}
            sx={{ mb: 8 }}
          />
        );
    }
  };
  
  return (
    <Box sx={{ p: 6, paddingBottom: 2 }}>
      {props.isLoading ? (
        <Typography>{t('exportAttributesTab.loading')}</Typography>
      ) : (
        <>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 3, 
          }}>
            {props.exportAttributes.map((attr, index) => (
              <Box key={`attribute-${attr.attributeDefinitionId}-${index}`}
                sx={{ 
                  flexBasis: { xs: '100%', sm: '45%', md: '30%' }, 
                  flexGrow: 1, 
                }}>
                {renderField(attr)}
              </Box>
            ))}
          </Box>
          
          <Box sx={{ mt: 4, pb: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant='secondary'
              size="lg"
              disabled={props.isLoading}
              onClick={() => setShowCancelConfirmationModal(true)}
            >
              {t('exportAttributesTab.cancel')}
            </Button>
            <Button variant='primary'
              size="lg"
              disabled={!hasPermissionTo(['ProductDetailExportAttributesEdit']) || props.isLoading || Object.keys(attributeValues).length === 0 || Object.values(validationErrors).some(error => !!error)}
              onClick={() => setShowConfirmationModal(true)}
              startIcon={<SvgIcon><Save01 fill='white' /></SvgIcon>}
            >
              {t('exportAttributesTab.save')}
            </Button>
          </Box>
        </>
      )}
      <ConfirmationDialog
        open={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={() => {
          saveAttributes();
          setShowConfirmationModal(false);
        }}
        icon={<Save01 />}
        variant='primary'
        title={t('Save Attributes')}
        message={t('exportAttributesTab.confirmMessage')}
        confirmButtonText={t('exportAttributesTab.yes')}
        cancelButtonText={t('exportAttributesTab.no')} />
      <ConfirmationDialog
        open={showCancelConfirmationModal}
        onCancel={() => setShowCancelConfirmationModal(false)}
        onConfirm={() => {
          discardChanges();
          setShowCancelConfirmationModal(false);
        }}
        icon={<SvgIcon><WarningIcon /></SvgIcon>}
        variant='bad'
        title={t('Discard Changes')}
        message={t('exportAttributesTab.confirmDiscardMessage')}
        confirmButtonText={t('exportAttributesTab.yes')}
        cancelButtonText={t('exportAttributesTab.no')}
      />
    </Box>
  );
};

export default ExportAttributes;